<script setup lang="ts">
import ArchetypePage from '~/components/archetypes/ArchetypePage.vue';

useSeoMeta({
  title: 'ISO 9001 Readiness Planning — QikSolve',
  description:
    'Prepare for ISO 9001 readiness by connecting leadership, process ownership, records, review, and improvement before treating it as a document update.',
});

const page = {
  hero: {
    eyebrow: 'ISO 9001 readiness planning',
    title: 'Prepare for ISO 9001 readiness without reducing it to paperwork',
    summary:
      'Readiness is not a document-production exercise. It is the practical work of understanding how leadership, processes, people, records, review, and improvement operate together.',
    cta: { label: 'Discuss your readiness context', to: '/contact' },
  },
  intro: {
    title: 'Start with the way work happens now',
    content:
      'An ISO 9001 readiness conversation is most useful when it begins with actual work rather than a clause-by-clause document check. Teams need to see how responsibilities are exercised, how decisions are recorded, how performance is reviewed, and how improvements are carried through the organisation.',
    items: [
      {
        title: 'Current practice',
        description: 'Identify the processes, handoffs, decisions, and records that already shape quality outcomes.',
      },
      {
        title: 'Ownership',
        description: 'Clarify who is accountable for process performance, documented information, review, and improvement.',
      },
      {
        title: 'Practical evidence',
        description: 'Focus on the records and review points that show how the operating model works in practice.',
      },
    ],
  },
  steps: {
    title: 'A practical readiness sequence',
    items: [
      {
        title: 'Define the intended quality outcome',
        description: 'Choose the customer, operational, or quality outcome the process is intended to deliver before selecting evidence or tools.',
      },
      {
        title: 'Map the operating reality',
        description: 'Review how work moves between people, systems, approvals, records, and decisions, including the local workarounds that have become normal.',
      },
      {
        title: 'Agree the review and improvement rhythm',
        description: 'Set meaningful points for performance review, risk consideration, corrective action, ownership, and continual improvement.',
      },
    ],
  },
  sections: [
    {
      title: 'Questions that make readiness useful',
      items: [
        {
          title: 'Is leadership direction visible?',
          description: 'Teams should be able to connect quality objectives, priorities, and decisions to the work they are expected to perform.',
        },
        {
          title: 'Do process owners have clear accountability?',
          description: 'Ownership should cover performance, decisions, records, issues, and improvement, not merely the title of a procedure.',
        },
        {
          title: 'Can people find and use the right information?',
          description: 'Documented information needs to support consistent work and informed decisions at the point it is needed.',
        },
        {
          title: 'Is improvement connected to evidence?',
          description: 'The organisation should be able to see what changed, why it changed, who reviewed it, and what it learned.',
        },
      ],
    },
    {
      title: 'How QikSolve supports readiness work',
      items: [
        {
          title: 'Service and implementation context',
          description: 'QikSolve helps teams frame the operating-model, process, governance, and evidence questions that should be resolved before a deeper implementation path is chosen.',
        },
        {
          title: 'Microsoft 365 governance context',
          description: 'Where appropriate, the work can consider how SharePoint and Microsoft 365 may support controlled information, workflows, access, and review.',
        },
        {
          title: 'A proportionate next step',
          description: 'A useful starting point may be one process or one recurring decision, rather than a broad attempt to redesign every document at once.',
        },
      ],
    },
  ],
  callout: {
    title: 'Readiness is not certification advice',
    content:
      'QikSolve is not a certification body and does not guarantee certification, audit outcomes, or compliance status. Your organisation remains responsible for its decisions, controls, approvals, and oversight.',
    tone: 'boundary' as const,
  },
  relatedLinks: {
    title: 'Continue the ISO 9001 conversation',
    items: [
      {
        label: 'ISO 9001 in practice',
        description: 'Return to the corporate hub for the wider practical context of ISO 9001.',
        to: '/product/iso-9001',
      },
      {
        label: 'Records and management review',
        description: 'Explore how documented information, review, and improvement should work together.',
        to: '/product/iso-9001-records-and-management-review',
      },
      {
        label: 'Practical GxP quality systems',
        description: 'Consider how a quality system can remain usable and sustainable in daily operations.',
        to: '/product/quality-systems',
      },
      {
        label: 'SharePoint governance',
        description: 'Explore the broader Microsoft 365 governance context for controlled information and access.',
        to: '/product/sharepoint-governance',
      },
      {
        label: 'Map and implement your quality process',
        description: 'Explore QikSolve delivery support for clarifying ownership, process design, and implementation readiness.',
        to: '/services/quality-process-mapping-development-and-implementation',
      },
    ],
  },
  cta: {
    title: 'Work from a real quality question',
    content:
      'Bring one process, review concern, or ownership question that is making readiness difficult. We can help clarify a proportionate next step.',
    label: 'Contact QikSolve',
    to: '/contact',
  },
};
</script>

<template>
  <ArchetypePage v-bind="page" />
</template>